import React, { useState, useEffect } from 'react';
import { 
    onAuthStateChanged, 
    signOut, 
    updatePassword, 
    reauthenticateWithCredential, // 👈 Ajoute ceci
    EmailAuthProvider,            // 👈 Ajoute ceci
    User 
} from "firebase/auth";import { collection, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useJsApiLoader } from '@react-google-maps/api';
import { auth, db, firebaseConfig } from './firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';

// Import de vos écrans et composants
import { LoginScreen } from './screens/LoginScreen';
import StudentManagement from './screens/StudentManagement';
import BusManagement from './screens/BusManagement';
import AttendanceReport from './screens/AttendanceReport';
import { AdminMap } from './screens/AdminMap';
import { DataDeletionPage } from './screens/DataDeletionPage';
import './App.css';
import { SchoolSettings } from './components/SchoolSettings';

// ✅ Import du composant PaymentSheet et son interface
import { PaymentSheet, PaymentRecord } from './screens/PaymentSheet';

// --- INTERFACES ---
export interface Student {
    id: string;
    name?: string;
    studentClass?: string;
    parentEmail?: string;
    parentPhone?: string;
    address?: string;
    assignedBusNumber?: string;
    assignedBusId?: string;
    latitude?: number;
    longitude?: number;
    homeLatitude?: number;
    homeLongitude?: number;
    morningTrip?: boolean;
    noonTrip?: boolean;
    afternoonTrip?: boolean;
    eveningTrip?: boolean;
}

export interface Bus {
    id: string;
    number: string;
    driverName: string;
    driverPhone?: string;
    driver2Name?: string;
    driver2Phone?: string;
    password?: string;
    capacity?: number;
    licensePlate?: string;
    status?: string;
    routeDescription?: string;
    isTracking?: boolean;
    latitude?: number;
    longitude?: number;
}

export interface School {
    id: string;
    schoolId?: string;
    schoolName?: string;
    latitude?: number;
    longitude?: number;
    schooladdress?: string;
    mustChangePassword?: boolean;
}

function App() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    const [user, setUser] = useState<User | null>(null);
    const [activeSchool, setActiveSchool] = useState<School | null>(null);
    const [activeTab, setActiveTab] = useState('students');
    const [students, setStudents] = useState<Student[]>([]);
    const [buses, setBuses] = useState<Bus[]>([]);

    // ✅ État pour le menu mobile
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // ✅ Parents (source de vérité pour isPro + studentNames)
    const [parents, setParents] = useState<any[]>([]);

    // États pour la popup de mot de passe
    const [showPassModal, setShowPassModal] = useState(false);
    const [newPass, setNewPass] = useState('');
    const [passLoading, setPassLoading] = useState(false);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: firebaseConfig.apiKey
    });

    // --- DETECTION URL DE SUPPRESSION ---
    if (window.location.pathname === '/data-safety') {
        return <DataDeletionPage />;
    }

    // Surveillance de l'état de connexion global
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                setActiveTab('students');
                const technicalId = localStorage.getItem('gpschoolbus_id');
                if (technicalId && !activeSchool) {
                    const schoolSnap = await getDoc(doc(db, "schools", technicalId));
                    if (schoolSnap.exists()) {
                        const schoolData = { id: schoolSnap.id, ...schoolSnap.data() } as School;
                        setActiveSchool(schoolData);
                        if (schoolData.mustChangePassword) {
                            setShowPassModal(true);
                        }
                    }
                }
            } else {
                setActiveSchool(null);
            }
        });
        return () => unsubscribe();
    }, [activeSchool]);

    // ✅ OPTIMISATION 1 : Écouteur STUDENTS
    useEffect(() => {
        if (!activeSchool?.id) return;
        const needsStudents = ['students', 'student-list', 'map', 'attendance', 'payments'].includes(activeTab);
        if (!needsStudents) return;

        const unsubStudents = onSnapshot(
            collection(db, "schools", activeSchool.id, "students"),
            (snap) => setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() } as Student)))
        );
        return () => unsubStudents();
    }, [activeSchool?.id, activeTab]);

    // ✅ OPTIMISATION 2 : Écouteur BUS
    useEffect(() => {
        if (!activeSchool?.id) return;
        const needsBuses = ['students', 'student-list', 'buses', 'map', 'attendance'].includes(activeTab);
        if (!needsBuses) return;

        const unsubBuses = onSnapshot(
            collection(db, "schools", activeSchool.id, "buses"),
            (snap) => setBuses(snap.docs.map(d => ({ id: d.id, ...d.data() } as Bus)))
        );
        return () => unsubBuses();
    }, [activeSchool?.id, activeTab]);

    // ✅ OPTIMISATION 3 : Écouteur PARENTS
    useEffect(() => {
        if (!activeSchool?.id) return;
        if (activeTab !== 'payments') return;

        const unsubParents = onSnapshot(
            collection(db, "schools", activeSchool.id, "parents"),
            (snap) => setParents(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        return () => unsubParents();
    }, [activeSchool?.id, activeTab]);

    // 💳 Lancer le paiement Lemon Squeezy
    const handleCheckout = async (paymentsToPay: PaymentRecord[]) => {
        if (!activeSchool?.id || paymentsToPay.length === 0) return;

        try {
            const functions = getFunctions(auth.app, 'us-central1');
            const createLemonSqueezyCheckout = httpsCallable(functions, 'createLemonSqueezyCheckout');

            const parentEmails = paymentsToPay
                .map((p) => p.parentEmail)
                .filter(Boolean) as string[];

            const result = await createLemonSqueezyCheckout({
                schoolDocId: activeSchool.id,
                parentEmails: parentEmails,
                origin: window.location.origin,
            });

            const data = result.data as any;

            if (data?.url) {
                localStorage.setItem('gpschoolbus_pending_pro', JSON.stringify(paymentsToPay));
                window.location.href = data.url;
            } else {
                alert(t('error_payment', 'Impossible de créer la session de paiement.'));
            }
        } catch (error: any) {
            alert(t('error_payment_connection', `Erreur de connexion au serveur : ${error.message || 'inconnue'}`));
        }
    };

    // 🎁 Offrir PRO manuellement
    const handleOfferProFree = async (payment: PaymentRecord) => {
        if (!activeSchool?.id || !payment.parentDocId) return;

        const confirmed = window.confirm(
            t('confirm_offer_pro_one', `Offrir la version PRO complète à ${payment.studentName} ?`)
        );
        if (!confirmed) return;

        try {
            await updateDoc(
                doc(db, "schools", activeSchool.id, "parents", payment.parentDocId),
                {
                    isPro: true,
                    pref_notif_demarrage: true,
                    pref_show_pins: true,
                    pref_show_dist_time: true,
                }
            );
        } catch (error) {
            alert(t('error_saving', "Erreur lors de l'opération."));
        }
    };

    // ✖ Retirer PRO
    const handleRevokePro = async (payment: PaymentRecord) => {
        if (!activeSchool?.id || !payment.parentDocId) return;

        try {
            await updateDoc(
                doc(db, "schools", activeSchool.id, "parents", payment.parentDocId),
                {
                    isPro: false,
                    pref_notif_demarrage: false,
                    pref_show_pins: false,
                    pref_show_dist_time: false,
                }
            );
        } catch (error) {
            console.error('❌ Erreur revoke PRO:', error);
        }
    };

    // 📄 Justificatif PRO
    const handleDownloadReceipt = (payment: PaymentRecord) => {
        const currency = 'MAD';
        const pricePerStudent = 42;

        const receiptContent = `
========================================
     PRO SUBSCRIPTION RECEIPT
           GPSCHOOLBUS
========================================
Student       : ${payment.studentName}
Class         : ${payment.studentClass || '-'}
Parent        : ${payment.parentEmail || '-'}
Status        : ⚡ PRO ACTIVE
Period        : ${payment.period}
Amount        : ${pricePerStudent.toFixed(2)} ${currency}
========================================
Activated Features:
  ✓ Live Google Maps tracking
  ✓ Bus departure notifications
  ✓ Proximity alerts (350m)
  ✓ Estimated Time of Arrival (ETA)
========================================
Document automatically generated by the
GPSchoolBus administration console.
========================================
    `.trim();

        const blob = new Blob([receiptContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PRO_${payment.studentName.replace(/\s/g, '_')}_${payment.period}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleUpdatePassword = async () => {
        if (newPass.length < 6) {
            alert(t('alert_pass_short'));
            return;
        }

        const oldPass = prompt(t('prompt_old_pass'));
        if (!oldPass) return;

        setPassLoading(true);
        try {
            const currentUser = auth.currentUser;
            if (currentUser && activeSchool) {
                const credential = EmailAuthProvider.credential(currentUser.email!, oldPass);
                await reauthenticateWithCredential(currentUser, credential);
                await updatePassword(currentUser, newPass);
                await updateDoc(doc(db, "schools", activeSchool.id), {
                    mustChangePassword: false
                });
                alert(t('alert_pass_success'));
                setShowPassModal(false);
                handleLogout();
            }
        } catch (error: any) {
            if (error.code === 'auth/wrong-password') {
                alert(t('err_wrong_pass'));
            } else {
                alert(t('err_update') + " : " + error.message);
            }
        } finally {
            setPassLoading(false);
        }
    };

    const handleLoginSuccess = async (firestoreId: string) => {
        localStorage.setItem('gpschoolbus_id', firestoreId);
        setActiveTab('students');
        try {
            const schoolSnap = await getDoc(doc(db, "schools", firestoreId));
            if (schoolSnap.exists()) {
                const schoolData = { id: schoolSnap.id, ...schoolSnap.data() } as School;
                setActiveSchool(schoolData);
                if (schoolData.mustChangePassword) {
                    setShowPassModal(true);
                }
            }
        } catch (err) {
            console.error("Erreur chargement école login:", err);
        }
    };

    const handleLogout = () => {
        signOut(auth);
        localStorage.removeItem('gpschoolbus_id');
        setActiveSchool(null);
        setUser(null);
    };

    if (!user || !activeSchool) {
        return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
    }

    // ✅ FONCTION UTILITAIRE POUR CHANGER D'ONGLET ET FERMER LE MENU SUR MOBILE
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setIsMobileMenuOpen(false);
    };

    return (
        <div className={`app-layout ${isRTL ? 'rtl-mode' : ''}`} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>

            {/* ✅ BOUTON MENU MOBILE (visible uniquement sur petit écran) */}
            <button 
                className="mobile-menu-btn" 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
                ☰ Menu
            </button>

            {showPassModal && (
                <div className="modal-overlay">
                    <div className="password-update-card" style={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <div className="stat-icon">🔒</div>
                        <h3>{t('pass_modal_title')}</h3>
                        <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '20px' }}>
                            {t('pass_modal_desc')}
                        </p>
                        <input
                            type="password"
                            placeholder={t('p_new_password')}
                            value={newPass}
                            onChange={(e) => setNewPass(e.target.value)}
                            className="input-styled-icon"
                            style={{ marginBottom: '20px', width: '100%', boxSizing: 'border-box' }}
                        />
                                                <button
                            onClick={handleUpdatePassword}
                            disabled={passLoading}
                            className="btn-valider-inscription"
                            style={{ width: '100%', padding: '15px', marginBottom: '10px' }}
                        >
                            {passLoading ? t('btn_updating') : t('btn_activate_account')}
                        </button>
                        
                        {/* 👇 AJOUT DU BOUTON ANNULER / DÉCONNEXION */}
                        <button
                            onClick={handleLogout}
                            style={{ 
                                width: '100%', 
                                padding: '12px', 
                                backgroundColor: 'transparent', 
                                color: '#e53e3e', 
                                border: '1px solid #e53e3e', 
                                borderRadius: '8px', 
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            Annuler et se déconnecter
                        </button>
                    </div>
                </div>
            )}

            {/* ✅ SIDEBAR (avec classe "open" si mobileMenuOpen) */}
            <aside className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
                <div className="sidebar-brand">
                    <small className="school-tag">{t('tag_admin')}</small>
                    <h2 className="school-name-display">{activeSchool.schoolName}</h2>
                </div>
                <nav className="sidebar-nav">
                    <button className={activeTab === 'students' ? 'active' : ''} onClick={() => handleTabChange('students')}>
                        📝 {t('nav_inscription')}
                    </button>
                    <button className={activeTab === 'student-list' ? 'active' : ''} onClick={() => handleTabChange('student-list')}>
                        📋 {t('nav_student_list')}
                    </button>
                    <button className={activeTab === 'buses' ? 'active' : ''} onClick={() => handleTabChange('buses')}>
                        🚌 {t('nav_bus_management')}
                    </button>
                    <button className={activeTab === 'attendance' ? 'active' : ''} onClick={() => handleTabChange('attendance')}>
                        📋 {t('nav_attendance_report')}
                    </button>
                    <button className={activeTab === 'map' ? 'active' : ''} onClick={() => handleTabChange('map')}>
                        🗺️ {t('nav_live_map')}
                    </button>
                    <button className={activeTab === 'payments' ? 'active' : ''} onClick={() => handleTabChange('payments')}>
                        💳 {t('nav_payments') || 'Paiements PRO'}
                    </button>
                    <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => handleTabChange('settings')}>
                        ⚙️ {t('menu_settings') || 'Paramètres'}
                    </button>
                </nav>
                <button className="btn-logout" onClick={handleLogout}>{t('btn_logout')}</button>
            </aside>

            {/* ✅ MAIN CONTENT (ferme le menu au clic) */}
            <main className="main-content" onClick={() => setIsMobileMenuOpen(false)}>
                <div style={{ display: activeTab === 'map' ? 'block' : 'none', height: '100%' }}>
                    {isLoaded && activeSchool && (
                        <AdminMap
                            school={activeSchool}
                            students={students}
                            buses={buses}
                        />
                    )}
                </div>

                {activeTab === 'students' && (
                    <StudentManagement
                        schoolId={activeSchool.id}
                        mode="form"
                        schoolName={activeSchool.schoolName!}
                        onCancel={() => setActiveTab('student-list')}
                    />
                )}
                {activeTab === 'student-list' && (
                    <StudentManagement
                        schoolId={activeSchool.id}
                        mode="list"
                        schoolName={activeSchool.schoolName!}
                    />
                )}
                {activeTab === 'buses' && (
                    <BusManagement
                        schoolId={activeSchool.id}
                        mode="list"
                        schoolName={activeSchool.schoolName!}
                        schoolTechnicalId={activeSchool.id}
                    />
                )}
                {activeTab === 'attendance' && <AttendanceReport schoolId={activeSchool.id} />}

                {activeTab === 'payments' && (
                    <PaymentSheet
                        parents={parents}
                        students={students}
                        onCheckout={handleCheckout}
                        onDownloadReceipt={handleDownloadReceipt}
                    />
                )}

                {activeTab === 'settings' && (
                    <SchoolSettings user={user} onNavigate={setActiveTab} />
                )}
            </main>
        </div>
    );
}

export default App;